import api, { apiMultipart, twUrl } from '../../utils/api';
import { BaseQueryParams } from '../Global.model';
import { QBOService } from '../Quickbooks/QBO.model';
import {
  LoadLocation,
  LoadSavePDFParams,
  ImportedLoad,
  LoadSearchParams,
  ShareType,
  LocationMilageResponse,
  InboxItemCount
} from '../../redux/api/load/load.model';
import { SearchCustomerOrderResponse } from '../../redux/api/customerOrder/customerOrder.model';
import { AxiosRequestConfig } from 'axios';
import { CreateLocationData } from '../../redux/api/location/location.model';
import { StopSignature } from '../../redux/api/load/load.model';
import {
  Order,
  OrderAttachment,
  OrderSearchFilterParams,
  EdiOrder,
  EdiOrderSearchResponse
} from '../../redux/api/orders/orders.model';
import { ShareHistory, Trip } from '../../redux/api/trips/trips.model';
import { FilterParams } from '../../redux/api/savedViews/savedViews.model';

export interface LoadPaginateResponse<T> {
  content: T[];
  totalItems: number;
}

export interface GetLoadsConfigOld {
  calendar?: boolean;
  paginated?: boolean;
}

export interface EdiLoadTenderSearchParams {
  load_tender_id?: string;
}

class LoadsService {
  /**
   * Gets EDI orders
   */
  static getEdiOrders(
    filterParams?: EdiLoadTenderSearchParams,
    paginatedParams?: BaseQueryParams,
    config?: GetLoadsConfigOld
  ): Promise<EdiOrderSearchResponse[] | EdiOrder[] | LoadPaginateResponse<EdiOrderSearchResponse>> {
    return new Promise((resolve, reject) => {
      const pagination = config?.paginated ? { ...paginatedParams } : {};
      const bodyParams = filterParams ? { ...filterParams } : undefined;
      const queryParams = {
        calendar: config?.calendar,
        ...pagination
      };

      const apiResponse = api.post(twUrl.getEdiOrders(), filterParams && bodyParams, {
        params: queryParams
      });

      apiResponse
        .then((response) => {
          if (config?.paginated) {
            resolve({
              content: response.data.data,
              totalItems: response.data.total
            });
          }
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Rejects (and deletes) an EDI order
   */
  static rejectEdiOrder(orderId: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.post(twUrl.rejectEdiOrder(orderId));

      apiResponse
        .then((response) => {
          resolve(response.status);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Gets forwarded emails to import as loads
   */
  static getForwardedEmails(): Promise<ImportedLoad[]> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.forwardedEmails);

      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Declines (and deletes) a forwarded email from becoming a load
   */
  static declineForwardedEmail(emailId: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.delete(twUrl.declineEmail(emailId));

      apiResponse
        .then((response) => {
          resolve(response.status);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Gets forwarded emails attachment file
   */
  static getEmailFile(emailId: string, attachmentId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.viewAttachment(emailId, attachmentId), {
        responseType: 'blob'
      });

      apiResponse
        .then((response) => resolve(response.data))
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Gets customer order data
   */
  static getCustomerOrderById(id: string): Promise<SearchCustomerOrderResponse> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.v3.getCustomerOrderById(id));

      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get inbox item count
   */
  static getInboxItemCount(): Promise<InboxItemCount> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.getInboxItemCount);

      apiResponse
        .then((response) => resolve(response.data.data))
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  static getAllDocumentsByOrderOrTrip(
    orderIds?: string[],
    tripIds?: string[]
  ): Promise<OrderAttachment[]> {
    return new Promise((resolve, reject) => {
      const body = {} as { tripId?: string[]; orderId?: string[] };
      if (tripIds) {
        body.tripId = tripIds;
      }
      if (orderIds) {
        body.orderId = orderIds;
      }

      const apiResponse = api.post('/trip-order-attachments', body);

      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Gets load file
   */
  static getLoadFile(key: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.viewFile(key), {
        responseType: 'blob'
      });

      apiResponse
        .then((response) => resolve(response.data))
        .catch((error) => {
          reject(error);
        });
    });
  }

  static getAttachmentFile(attachmentId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.viewFile(attachmentId), {
        responseType: 'blob'
      });

      apiResponse
        .then((response) => resolve(response.data))
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Gets user's location list
   */
  static getLocations(baseQueryParams?: BaseQueryParams): Promise<LoadLocation[]> {
    return new Promise((resolve, reject) => {
      const queryParams = { ...baseQueryParams };
      const apiResponse = api.get(twUrl.locations, {
        params: queryParams
      });

      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Gets estimated distance between locations
   * @param pickupLocationId pickup location id
   * @param deliveryLocationId delivery location id
   * @param intermediateStopLocationIds optional intermediate stops location ids
   */
  static getEstimatedMilage(
    pickupLocationId: string,
    deliveryLocationId: string,
    intermediateStopLocationIds?: string[]
  ): Promise<LocationMilageResponse> {
    return new Promise((resolve, reject) => {
      const params = {
        pickup_location_id: pickupLocationId,
        delivery_location_id: deliveryLocationId,
        intermediate_stop_ids: intermediateStopLocationIds
      };

      const apiResponse = api.post(twUrl.getEstimatedMilage, params);
      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Creates new location
   * @param location
   */
  static createLocation(location: CreateLocationData): Promise<LoadLocation> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.post(twUrl.locations, location);

      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Generates a PDF from a load
   * @param id
   * @param attachments
   * @returns Blob
   */
  static saveAsPDF(id: string, attachments: LoadSavePDFParams): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.post(twUrl.saveAsPDF(id), attachments, {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf'
        }
      });

      apiResponse
        .then((response) => {
          resolve(new Blob([response.data]));
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Returns a CSV of results after importing Fuel transactions
   * @returns CSV file
   */
  static importFuelTransactionsCSV(doc: File, source: 'tb' | 'efs'): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();

      formData.append('file', doc);

      const apiResponse = apiMultipart.post(twUrl.v3.importFuelTransactionsCSV(source), formData, {
        responseType: 'text',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/csv'
        }
      });

      apiResponse
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (error.response.status === 400) {
            reject({
              message: error.response.data.message,
              document: new Blob([error.response.data])
            });
          } else {
            reject(error.response.data);
          }
        });
    });
  }

  /**
   * Returns a list of loads that can be used for a multi-load invoice
   */
  static canInvoiceBeCreated(body: FilterParams, config?: AxiosRequestConfig): Promise<Order[]> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.post(twUrl.v3.canInvoiceBeCreated, body, config);

      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          if (error.response) {
            return reject(error.response.data);
          }

          reject(error);
        });
    });
  }

  /**
   * @description service to get shared history of load
   * @param idLoad id of load whose shared history is fetched
   * @param shareType type of share history to fetch
   */
  static getShareHistory(idLoad: string, shareType?: ShareType): Promise<ShareHistory[]> {
    return new Promise((resolve, reject) => {
      const request = api.get(twUrl.shareHistory(idLoad), {
        params: { shareType }
      });
      request
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => reject(error.response.data));
    });
  }

  /**
   * @description service to generate rate confirmation pdf for a load
   * @param loadId id of load whose rate confirmation pdf is generated
   */
  static generateRateConfirmationPdf(loadId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.v3.generateRateConfirmationPdf(loadId), {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf'
        }
      });

      apiResponse
        .then((response) => {
          resolve(new Blob([response.data]));
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Saves signature for a stop
   * @param signatureFile
   * @param loadId
   * @param stopId
   */
  static saveSignature(
    signatureFile: Blob,
    loadId: string,
    stopId: string,
    fullName: string
  ): Promise<number> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();

      formData.append('signature', signatureFile);
      formData.append('full_name', fullName);

      const apiResponse = apiMultipart.post(twUrl.v3.saveSignature(stopId, loadId), formData);

      apiResponse
        .then((response) => {
          resolve(response.status);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  static generateBolPdf(loadId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.v3.generateBolPdf(loadId), {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf'
        }
      });

      apiResponse
        .then((response) => {
          resolve(new Blob([response.data]));
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  static getOrderById(id: string): Promise<Order> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(`/orders/${id}`);

      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  static getTripById(id: string): Promise<Trip> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(`/trips/${id}`);

      apiResponse
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  static generateTripManifest(tripId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.tripManifest(tripId), {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf'
        }
      });

      apiResponse
        .then((response) => {
          resolve(new Blob([response.data]));
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }
}

export default LoadsService;
